import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMeetings } from './hooks/useMeetings';
import { useAuth } from './hooks/useAuth';
import { Calendar, Clock, MapPin, Users, Edit, Trash2, Eye, EyeOff, X, Paperclip, Link2, User, ListFilter, CheckCircle, CalendarClock, History, UserIcon } from 'lucide-react';
import Spinner from './components/Spinner';
import AttendeesModal from './components/AttendeesModal';
import { Meeting, MeetingFormData, defaultFormData } from './models/Content';
import { Timestamp } from 'firebase/firestore';
import { addPost, updatePost } from './data/contentDatabase';
import { addMeetingAttendee, removeMeetingAttendee, getConfirmedAttendeeCount } from './data/meetingAttendeesDatabase';

import { Attachment } from './models/Content';
import AttachmentsDisplay from './components/AttachmentsDisplay';
import { getAllUsers } from './data/userDatabase.ts';
import MeetingAttendanceButton from './components/MeetingAttendanceButton';
import SendQuestionButton from './components/shared/SendQuestionButton';
import { getTrackingInfo, logPostActivity } from './utils/trackingUtils';
import { Bell, Settings, TrendingUp, PlusCircle, Shield, LogOut, UserCog } from 'lucide-react';
import { User as UserModel } from './models/User';
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Avatar, AvatarFallback } from './components/ui/avatar';
import { format } from 'date-fns';
import PostActions from './components/shared/PostActions';
import { getFunctions, httpsCallable } from 'firebase/functions';
import MeetingEditForm from './components/MeetingEditForm';
import { formatDate, formatTime } from './utils/dateUtils';
import { getCurrentTimezone, getLocale, getCurrentDateFormat } from './config/settings';

// Global cache for users to persist across navigation
const usersCache = {
  users: {} as { [id: string]: string },
  isLoaded: false,
};



const Meetings: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, isAdmin, isSuperAdmin } = useAuth();
  const {
    activeTab,
    setActiveTab,
    userAttendance,
    setUserAttendance,
    items: meetings,
    setItems: setMeetings,
    isLoading,
    error,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleAttendanceChange,
    getFilteredMeetings,
    getMeetingTypeLabel,
    getMeetingTypeColor,
    getVenueIcon,
  } = useMeetings();

  const [showNewMeetingForm, setShowNewMeetingForm] = useState(false);
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<MeetingFormData>(defaultFormData);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [users, setUsers] = useState<{ [id: string]: string }>(usersCache.users);
  const [confirmedAttendeeCounts, setConfirmedAttendeeCounts] = useState<{ [meetingId: string]: number }>({});
  const [showCalendarInvitePopup, setShowCalendarInvitePopup] = useState(false);
  const [selectedMeetingForInvite, setSelectedMeetingForInvite] = useState<Meeting | null>(null);
  const [isSendingInvite, setIsSendingInvite] = useState(false);

  // Custom date formatting function that shows yyyy-mm-dd with timezone
  const formatDateTimeWithTimezone = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    const timezone = getCurrentTimezone();
    const formattedDate = formatDate(timestamp);
    const formattedTime = formatTime(date.toISOString());
    
    // Get timezone abbreviation
    const locale = getLocale();
    const timezoneName = Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || timezone;
    
    return `${formattedDate} ${formattedTime} ${timezoneName}`;
  };

  // Helper function to convert user-formatted date to ISO format for storage
  const convertUserDateToISO = (userDate: string): string => {
    if (!userDate) return '';
    
    const dateFormat = getCurrentDateFormat();
    let date: Date;
    
    try {
      switch (dateFormat) {
        case 'mm/dd/yyyy': {
          const [month, day, year] = userDate.split('/');
          date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
          break;
        }
        case 'dd/mm/yyyy': {
          const [day, month, year] = userDate.split('/');
          date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
          break;
        }
        case 'yyyy-mm-dd':
        default: {
          date = new Date(userDate);
          break;
        }
      }
      
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', userDate);
        return '';
      }
      
      // Return in YYYY-MM-DD format for storage
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error converting date:', error);
      return '';
    }
  };

  useEffect(() => {
    // Only load users if not already cached
    if (!usersCache.isLoaded) {
      getAllUsers().then(userList => {
        const userMap: { [id: string]: string } = {};
        userList.forEach(u => { userMap[u.id] = u.fullName; });
        setUsers(userMap);
        usersCache.users = userMap;
        usersCache.isLoaded = true;
      });
    }
  }, []);

  // Cache attendee counts to prevent refetching
  const [attendeeCountsLoaded, setAttendeeCountsLoaded] = useState(new Set<string>());

  const fetchConfirmedAttendeeCounts = useCallback(async (meetingsList: any[]) => {
    const counts: { [meetingId: string]: number } = {};
    
    // Only fetch counts for meetings that haven't been loaded yet
    const meetingsToFetch = meetingsList.filter(meeting => 
      meeting.id && !attendeeCountsLoaded.has(meeting.id)
    );
    
    if (meetingsToFetch.length === 0) {
      return; // No new meetings to fetch
    }
    
    // Fetch attendee counts in parallel for better performance
    await Promise.all(meetingsToFetch.map(async (meeting) => {
      if (meeting.id) {
        counts[meeting.id] = await getConfirmedAttendeeCount(meeting.id);
      }
    }));
    
    setConfirmedAttendeeCounts(prev => ({
      ...prev,
      ...counts
    }));
    
    // Mark these meetings as loaded
    setAttendeeCountsLoaded(prev => {
      const newSet = new Set(prev);
      meetingsToFetch.forEach(meeting => {
        if (meeting.id) newSet.add(meeting.id);
      });
      return newSet;
    });
  }, [attendeeCountsLoaded]);

  // Memoize filtered meetings to prevent unnecessary recalculations
  const filteredMeetings = useMemo(() => getFilteredMeetings(user), [getFilteredMeetings, user, activeTab]);

  useEffect(() => {
    if (filteredMeetings.length > 0) {
      fetchConfirmedAttendeeCounts(filteredMeetings);
    }
  }, [filteredMeetings, fetchConfirmedAttendeeCounts]);

  if (authLoading || isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="text-gray-500">Please log in to view meetings</div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };



  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isAdmin || !user || !user.id) {
      console.error('User not properly authenticated');
      return;
    }
    
    try {
      // Convert user-formatted date to ISO format for storage
      const formDataWithISODate = {
        ...formData,
        date: convertUserDateToISO(formData.date)
      };
      
      if (editingMeetingId) {
        // Use the hook's handleEdit function for optimistic updates
        await handleEdit(editingMeetingId, formDataWithISODate, attachments);
      } else {
        // Use the hook's handleSubmit function for optimistic updates
        await handleSubmit(formDataWithISODate, attachments);
      }
      
      setShowNewMeetingForm(false);
      setEditingMeetingId(null);
      setFormData(defaultFormData);
      setAttachments([]);
    } catch (error) {
      console.error('Failed to save meeting post:', error);
    }
  };

  const handleEditClick = (meeting: Meeting) => {
    // Convert stored ISO date to user's preferred format
    const formattedDate = meeting.date ? formatDate(meeting.date) : meeting.date;
    
    setFormData({
      type: meeting.type,
      title: meeting.title,
      date: formattedDate,
      time: meeting.time,
      timezone: meeting.timezone,
      venue: meeting.venue,
      physicalVenue: meeting.physicalVenue,
      meetingLink: meeting.meetingLink,
      agenda: meeting.agenda,
      additionalInfo: meeting.additionalInfo,
      contactEmail: meeting.contactEmail,
      contactPhone: meeting.contactPhone
    });
    setAttachments(meeting.attachments || []);
    setEditingMeetingId(meeting.id || null);
    setShowNewMeetingForm(true);
  };

  const handleDeleteClick = async (meeting: Meeting) => {
    if (window.confirm('Are you sure you want to delete this meeting?')) {
      if (meeting.id) {
        await handleDelete(meeting.id);
      }
    }
  };

  const handleHideClick = async (meeting: Meeting) => {
    try {
      // Optimistically update the local state for immediate visual feedback
      const updatedMeeting = { ...meeting, hidden: !meeting.hidden };
      const updatedMeetings = meetings.map(m => m.id === meeting.id ? updatedMeeting : m);
      setMeetings(updatedMeetings);
      
      // Then update the database
      await updatePost(updatedMeeting);
    } catch (error) {
      console.error('Failed to hide/unhide meeting:', error);
      // Revert the optimistic update on error
      const revertedMeetings = meetings.map(m => m.id === meeting.id ? meeting : m);
      setMeetings(revertedMeetings);
    }
  };

  const handleAttendanceTypeChange = async (meetingId: string, type: 'in-person' | 'virtual' | 'not-attending' | 'unconfirmed') => {
    try {
      // Update attendance using the hook which properly handles unconfirmed state
      await handleAttendanceChange(meetingId, type);
      
      // Only update the attendee count for this specific meeting, not all meetings
      const newCount = await getConfirmedAttendeeCount(meetingId);
      setConfirmedAttendeeCounts(prev => ({
        ...prev,
        [meetingId]: newCount
      }));
    } catch (error) {
      console.error('Failed to update attendance:', error);
    }
  };

  const handleSendCalendarInvite = async () => {
    if (!selectedMeetingForInvite || !user) return;

    setIsSendingInvite(true);
    try {
      const functions = getFunctions();
      const sendCalendarInviteFn = httpsCallable(functions, 'sendCalendarInvite');
      await sendCalendarInviteFn({ userId: user.id, meetingId: selectedMeetingForInvite.id });
      // Optionally, show a success toast
      // Calendar invite sent successfully
    } catch (error) {
      console.error('Failed to send calendar invite:', error);
      // Optionally, show an error toast
      // Failed to send calendar invite
    } finally {
      setIsSendingInvite(false);
      setShowCalendarInvitePopup(false);
      setSelectedMeetingForInvite(null);
    }
  };

  const handleOpenCalendarInvite = (meeting: Meeting) => {
    setSelectedMeetingForInvite(meeting);
    setShowCalendarInvitePopup(true);
  };

  const handleCloseCalendarInvite = () => {
    setShowCalendarInvitePopup(false);
    setSelectedMeetingForInvite(null);
  };

  // Add a function to get the background color for the meeting type


  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Meetings</h1>
            <p className="text-slate-600 mt-2">Schedule and track shareholder meetings</p>
          </div>
          {user && isAdmin && (
            <Button
              onClick={() => {
                setEditingMeetingId(null);
                setFormData(defaultFormData);
                setShowNewMeetingForm(true);
              }}
              variant="outline"
              className="border-slate-300 text-slate-800 hover:bg-slate-50 hover:text-slate-900 h-auto px-4 py-1 text-sm font-medium rounded-lg"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              New Meeting
            </Button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="border-b border-slate-200 mb-8">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`whitespace-nowrap py-4 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center transition-colors min-w-fit flex-shrink-0 touch-manipulation ${
                activeTab === 'upcoming'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <CalendarClock className="w-4 h-4 mr-2" />
              Upcoming
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`whitespace-nowrap py-4 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center transition-colors min-w-fit flex-shrink-0 touch-manipulation ${
                activeTab === 'past'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <History className="w-4 h-4 mr-2" />
              Past
            </button>
            <button
              onClick={() => setActiveTab('my-attendance')}
              className={`whitespace-nowrap py-4 px-2 sm:px-1 border-b-2 font-medium text-sm flex items-center transition-colors min-w-fit flex-shrink-0 touch-manipulation ${
                activeTab === 'my-attendance'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">My Attendance</span>
              <span className="sm:hidden">Attendance</span>
            </button>
          </nav>
        </div>

        {showNewMeetingForm && !editingMeetingId && (
          <MeetingEditForm
            formData={formData}
            attachments={attachments}
            isEditing={false}
            onInputChange={handleInputChange}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowNewMeetingForm(false);
              setFormData(defaultFormData);
              setAttachments([]);
            }}
            setAttachments={setAttachments}
          />
        )}

        <div className="space-y-6">
          {filteredMeetings.map((meeting) => (
            <div key={meeting.id}>
              {/* Show edit form inline when editing this specific meeting */}
              {editingMeetingId === meeting.id && (
                <MeetingEditForm
                  formData={formData}
                  attachments={attachments}
                  isEditing={true}
                  onInputChange={handleInputChange}
                  onSubmit={handleFormSubmit}
                  onCancel={() => {
                    setEditingMeetingId(null);
                    setShowNewMeetingForm(false);
                    setFormData(defaultFormData);
                    setAttachments([]);
                  }}
                  setAttachments={setAttachments}
                />
              )}
              
              {/* Show meeting card when not editing */}
              {editingMeetingId !== meeting.id && (
                <Card className="overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200 border-0 rounded-xl">
                  <div className="flex flex-col lg:flex-row">
                    {/* Enhanced Date Sidebar */}
                    <div className="lg:w-1/5 bg-gradient-to-br from-slate-50 to-slate-100 p-6 lg:p-8 flex flex-col items-center justify-start border-r border-slate-200 min-h-[200px]">
                      <div className="text-center space-y-3">
                        <div className={`text-4xl lg:text-5xl font-bold ${getMeetingTypeColor(meeting.type)} mb-2`}>
                          {format(new Date(meeting.date), 'dd')}
                        </div>
                        <div className="text-sm font-medium text-slate-600 uppercase tracking-wide">
                          {format(new Date(meeting.date), 'MMM yyyy')}
                        </div>
                        <div className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                          meeting.type === 'AGM' || meeting.type === 'Norway AGM' 
                            ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                            : meeting.type === 'EGM' || meeting.type === 'Norway EGM'
                            ? 'bg-purple-100 text-purple-800 border border-purple-200'
                            : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                        }`}>
                          {getMeetingTypeLabel(meeting.type)}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 p-6 lg:p-8">
                      {/* Header with Actions */}
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6">
                        <div className="flex-1 mb-4 sm:mb-0">
                          <h2 className="text-lg font-bold text-slate-900 mb-2 leading-tight">{meeting.title}</h2>
                          <div className="flex items-center text-sm text-slate-600">
                            <UserIcon className="w-4 h-4 mr-2 text-slate-400" />
                            <span className="font-medium">{users[meeting.authorId] || 'Unknown Author'}</span>
                          </div>
                        </div>
                        {user && (
                          <div className="flex-shrink-0">
                            <PostActions
                              isAdmin={isAdmin}
                              isSuperAdmin={isSuperAdmin}
                              hidden={!!meeting.hidden}
                              onEdit={() => handleEditClick(meeting)}
                              onDelete={() => handleDeleteClick(meeting)}
                              onHide={() => handleHideClick(meeting)}
                              onUnhide={() => handleHideClick(meeting)}
                            />
                          </div>
                        )}
                      </div>

                      {/* Meeting Details Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center text-sm text-slate-700">
                          <Clock className="w-4 h-4 mr-3 text-slate-500 flex-shrink-0" />
                          <span className="font-medium">{meeting.time}</span>
                          <span className="ml-1 text-slate-500">({meeting.timezone})</span>
                        </div>
                        
                        <div className="flex items-center text-sm text-slate-700">
                          {React.createElement(getVenueIcon(meeting.venue), { 
                            className: "w-4 h-4 mr-3 text-slate-500 flex-shrink-0" 
                          })}
                          <span className="font-medium">{meeting.venue}</span>
                        </div>

                        {meeting.venue !== 'Virtual' && meeting.physicalVenue && (
                          <div className="flex items-center text-sm text-slate-700">
                            <MapPin className="w-4 h-4 mr-3 text-slate-500 flex-shrink-0" />
                            <span className="truncate">{meeting.physicalVenue}</span>
                          </div>
                        )}

                        {(meeting.venue === 'Virtual' || meeting.venue === 'Hybrid') && meeting.meetingLink && (
                          <div className="flex items-center text-sm text-slate-700">
                            <Link2 className="w-4 h-4 mr-3 text-slate-500 flex-shrink-0" />
                            <a 
                              href={meeting.meetingLink} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2 truncate"
                            >
                              Join Meeting
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Content Sections */}
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                            <div className="w-1 h-5 bg-blue-500 rounded-full mr-3"></div>
                            Agenda
                          </h3>
                          <div className="text-slate-700 leading-relaxed whitespace-pre-wrap pl-4">
                            {meeting.agenda}
                          </div>
                        </div>

                        {meeting.additionalInfo && (
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                              <div className="w-1 h-5 bg-emerald-500 rounded-full mr-3"></div>
                              Additional Information
                            </h3>
                            <div className="text-slate-700 leading-relaxed whitespace-pre-wrap pl-4">
                              {meeting.additionalInfo}
                            </div>
                          </div>
                        )}

                        {(meeting.attachments || []).length > 0 && (
                          <div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                              <div className="w-1 h-5 bg-purple-500 rounded-full mr-3"></div>
                              Attachments
                            </h3>
                            <div className="pl-4">
                              <AttachmentsDisplay attachments={meeting.attachments || []} />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Footer */}
                      <div className="mt-8 pt-6 border-t border-slate-200">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                          {/* Left side - Attendance */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <MeetingAttendanceButton 
                              meeting={{ id: meeting.id!, venue: meeting.venue }}
                              attendanceStatus={userAttendance[meeting.id!] || undefined}
                              onAttend={(type: 'in-person' | 'virtual' | 'not-attending' | 'unconfirmed') => handleAttendanceTypeChange(meeting.id!, type)}
                              solidBackground={!!userAttendance[meeting.id!] && userAttendance[meeting.id!] !== 'not-attending'}
                            />
                            <button 
                              onClick={() => setSelectedMeetingId(meeting.id!)}
                              className="flex items-center text-sm text-slate-600 hover:text-slate-900 transition-colors px-3 py-2 rounded-lg hover:bg-slate-100"
                            >
                              <Users className="w-4 h-4 mr-2" />
                              <span className="font-medium">{confirmedAttendeeCounts[meeting.id!] || 0}</span>
                              <span className="ml-1">confirmed</span>
                            </button>
                          </div>

                          {/* Right side - Actions */}
                          <div className="flex items-center gap-2">
                            <SendQuestionButton authorId={meeting.authorId} source="Meetings" />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenCalendarInvite(meeting)}
                              className="flex items-center gap-2 h-9 px-4 text-sm font-medium"
                            >
                              <Calendar className="w-4 h-4" />
                              Add to Calendar
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          ))}
        </div>
      </div>

      {selectedMeetingId && (
        <AttendeesModal
          meetingId={selectedMeetingId}
          onClose={() => setSelectedMeetingId(null)}
        />
      )}
      
      {showCalendarInvitePopup && selectedMeetingForInvite && (
        <>
          {/* Modal backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={handleCloseCalendarInvite}
          />
          {/* Modal content */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div 
              className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">Send Calendar Invite</h3>
              <p className="mb-6">An invite for "{selectedMeetingForInvite.title}" will be sent to your email.</p>
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={handleCloseCalendarInvite} disabled={isSendingInvite}>
                  Cancel
                </Button>
                <Button onClick={handleSendCalendarInvite} disabled={isSendingInvite}>
                  {isSendingInvite ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-blue-600"></div>
                      <span>Sending...</span>
                    </div>
                  ) : 'Send Invite'}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Meetings; 